/**
 * "Ask Vera about this" — the empty-state affordance that hands a pre-filled, EDITABLE
 * question to the Ask panel, rather than sending one on the user's behalf (VRG-227).
 *
 * A filtered-to-zero list is the single moment a user is most likely to want to ask a
 * question, and until this component existed the surface offered them nothing but the
 * filters they already know didn't work.
 *
 * Uses `prefill`, never `ask` — the empty state can only guess a REASONABLE default
 * question for the surface it sits on, not necessarily the exact thing narrowed by
 * whatever filters produced zero rows, so the user sees it in the composer and can edit
 * or send it. See `useAskStore`'s own `ask` vs `prefill` docblocks for why that
 * distinction exists at all: sending blind spends a query on a question the user never
 * quite asked.
 *
 * `question` MUST be something the ask grammar can actually answer —
 * `apps/api/src/features/ask/ask.tools.ts`'s `ALLOWED_METRIC_DIMENSIONS` /
 * `ALLOWED_METRIC_FILTERS` are the authority. Prefer a question already proven against
 * that grammar (the corpus in `apps/platform/src/ask/suggestedQuestions.ts` is the
 * reference list) over inventing a new one — that module's own docblock: a suggestion
 * that fails teaches a first-time user the feature doesn't work, which is worse than no
 * suggestion at all.
 */
import { Button } from '@repo/ui'

import { useAskStore } from 'ask'
import { AskVeraIcon } from 'components/display/AskVeraIcon/AskVeraIcon'

export type AskAboutButtonProps = {
  /** The question to prefill into the Ask composer, unsent. Must be answerable — see above. */
  question: string
  /** Defaults to "Ask Vera" — override when a more specific call to action reads better. */
  label?: string
}

export const AskAboutButton = ({ question, label = 'Ask Vera' }: AskAboutButtonProps) => {
  const prefill = useAskStore(s => s.prefill)

  return (
    <Button
      variant="secondary"
      disableElevation
      startIcon={<AskVeraIcon size={16} />}
      onClick={() => prefill(question)}
    >
      {label}
    </Button>
  )
}
